import React, { useState, useEffect } from "react";
import api from "../../config/URL";
import { toast } from "react-toastify";
import { format, startOfMonth, addMonths } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import fetchAllCentersWithIds from "../List/CenterList";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";
import fetchAllClassesWithIdsC from "../List/ClassListByCourse";
import fetchAllTeacherListByCenter from "../List/TeacherListByCenter";
import { Link, useLocation } from "react-router-dom";

function StudentAttendanceReport({ selectedCenter }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const studentId = searchParams.get("studentId");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [centerData, setCenterData] = useState([]);
  const [courseData, setCourseData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [teacherData, setTeacherData] = useState([]);
  const [filters, setFilters] = useState({
    centerId: selectedCenter || "",
    courseId: "",
    classId: "",
    teacherId: "",
    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(addMonths(startOfMonth(new Date()), 1), "yyyy-MM-dd"),
  });

  // Fetch center data on component mount
  const fetchCenterData = async () => {
    try {
      const centers = await fetchAllCentersWithIds();
      setCenterData(centers);
      // Set default centerId if selectedCenter is provided or first center in list
      if (selectedCenter) {
        setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
      } else if (centers.length > 0) {
        setFilters((prev) => ({ ...prev, centerId: centers[0].id }));
      }
    } catch (error) {
      toast.error("Error fetching centers: " + error.message);
    }
  };

  // Fetch courses and teachers when centerId changes
  const fetchListWithCenterIdData = async (centerId) => {
    if (!centerId) return;
    try {
      const [courseDatas, teacherDatas] = await Promise.all([
        fetchAllCoursesWithIdsC(centerId),
        fetchAllTeacherListByCenter(centerId),
      ]);
      setCourseData(courseDatas);
      setTeacherData(teacherDatas);
      setClassData([]); // Clear class data when center changes
      setFilters((prev) => ({ ...prev, courseId: "", classId: "" })); // Reset course and class filters
    } catch (error) {
      toast.error("Error fetching courses or teachers: " + error.message);
    }
  };

  // Fetch classes when courseId changes
  const fetchClassesWithCourseId = async (courseId) => {
    if (!courseId) {
      setClassData([]);
      setFilters((prev) => ({ ...prev, classId: "" }));
      return;
    }
    try {
      const classes = await fetchAllClassesWithIdsC(courseId);
      setClassData(classes);
      setFilters((prev) => ({ ...prev, classId: "" })); // Reset class filter
    } catch (error) {
      toast.error("Error fetching classes: " + error.message);
    }
  };

  const mapProgressResponseToAttendanceTable = (classes = []) => {
    return classes.map((cls) => ({
      classCode: cls.classCode,
      students: (cls.students || []).map((st) => {
        const attendance = {};

        (st.lessons || []).forEach((lesson) => {
          if (!lesson.lessonDate) return;

          attendance[lesson.lessonDate] = {
            status: lesson.isReplacement ? "R" : "P",
            isReplacement: lesson.isReplacement,
            replacementClassCode: lesson.replacementClassCode || "",
            curriculumCode: lesson.curriculumCode || "",
          };
        });

        return {
          studentId: st.studentId,
          studentUniqueId: st.studentUniqueId,
          studentName: st.studentName,
          attendance,
        };
      }),
    }));
  };

  // Fetch attendance data
  const fetchAttendanceData = async () => {
    if (!filters.centerId || !filters.startDate || !filters.endDate) {
      toast.warning("Please select Centre & Dates");
      return;
    }
    try {
      setLoading(true);
      const params = {
        centerId: filters.centerId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      if (filters.courseId) params.courseId = filters.courseId;
      if (filters.classId) params.classId = filters.classId;
      if (filters.teacherId) params.teacherId = filters.teacherId;

      // ✅ ADD THIS (correct way)
      if (studentId) {
        params.studentId = studentId;
      }
      const res = await api.get(`/getProgressReport`, { params });

      const mappedData = mapProgressResponseToAttendanceTable(
        res.data.classes || [],
      );
      setReportData(mappedData);
    } catch (err) {
      toast.error("Error fetching attendance data");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchCenterData();
  }, []);

  // Fetch courses and teachers when centerId changes
  useEffect(() => {
    fetchListWithCenterIdData(filters.centerId);
  }, [filters.centerId]);

  // Fetch classes when courseId changes
  useEffect(() => {
    fetchClassesWithCourseId(filters.courseId);
  }, [filters.courseId]);

  // Fetch attendance data when filters change
  useEffect(() => {
    if (filters.centerId) {
      fetchAttendanceData();
    }
  }, [
    filters.centerId,
    filters.courseId,
    filters.classId,
    filters.teacherId,
    filters.startDate,
    filters.endDate,
  ]);

  const clearFilter = () => {
    const newFilters = {
      centerId:
        selectedCenter || (centerData.length > 0 ? centerData[0].id : ""),
      courseId: "",
      classId: "",
      teacherId: "",
      startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
      endDate: format(addMonths(startOfMonth(new Date()), 1), "yyyy-MM-dd"),
    };
    setFilters(newFilters);
    // setCourseData([]);
    setClassData([]);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCenterChange = (e) => {
    const centerId = e.target.value;
    setFilters((prev) => ({
      ...prev,
      centerId,
      courseId: "",
      classId: "",
      teacherId: "",
    }));
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setFilters((prev) => ({
      ...prev,
      courseId,
      classId: "",
    }));
  };

  const getClassDates = (students = []) => {
    const dateSet = new Set();

    students.forEach((st) => {
      if (!st.attendance) return;
      Object.keys(st.attendance).forEach((date) => {
        dateSet.add(date);
      });
    });

    return Array.from(dateSet).sort();
  };

  return (
    <div className="container my-3">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Student Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Student Attendance Report
        </li>
      </ol>
      <div className="row mb-3 g-2">
        {/* First row of filters */}
        <div className="col-12 col-lg-auto">
          <select
            className="form-select form-select-sm center_list"
            name="centerId"
            style={{ width: "100%" }}
            onChange={handleCenterChange}
            value={filters.centerId}
          >
            <option value="" disabled>
              Select the Centre
            </option>
            {centerData?.map((center) => (
              <option key={center.id} value={center.id}>
                {center.centerNames}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12 col-lg-auto">
          <select
            className="form-select form-select-sm center_list"
            name="courseId"
            style={{ width: "100%" }}
            onChange={handleCourseChange}
            value={filters.courseId}
          >
            <option value="" disabled>
              Select the Course
            </option>
            {courseData?.map((course) => (
              <option key={course.id} value={course.id}>
                {course.courseNames}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12 col-lg-auto">
          <select
            className="form-select form-select-sm center_list"
            name="classId"
            style={{ width: "100%" }}
            onChange={(e) => handleFilterChange("classId", e.target.value)}
            value={filters.classId}
          >
            <option value="" disabled>
              Select the Class
            </option>
            {classData?.map((classes) => (
              <option key={classes.id} value={classes.id}>
                {classes.classNames}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12 col-lg-auto">
          <select
            className="form-select form-select-sm center_list"
            name="teacherId"
            style={{ width: "100%" }}
            onChange={(e) => handleFilterChange("teacherId", e.target.value)}
            value={filters.teacherId}
          >
            <option value="" disabled>
              Select the Teacher
            </option>
            {teacherData?.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.teacherNames}
              </option>
            ))}
          </select>
        </div>

        {/* Date pickers */}
        <div className="col-12 col-md-auto">
          <DatePicker
            dateFormat="yyyy-MM-dd"
            className="form-control form-control-sm center_list ps-5"
            name="startDate"
            showMonthDropdown
            showYearDropdown
            selected={filters.startDate ? new Date(filters.startDate) : null}
            onChange={(date) =>
              handleFilterChange(
                "startDate",
                date ? format(date, "yyyy-MM-dd") : "",
              )
            }
            placeholderText="Start Date"
          />
        </div>

        <div className="col-12 col-md-auto">
          <DatePicker
            dateFormat="yyyy-MM-dd"
            className="form-control form-control-sm center_list ps-5"
            name="endDate"
            showMonthDropdown
            showYearDropdown
            selected={filters.endDate ? new Date(filters.endDate) : null}
            onChange={(date) =>
              handleFilterChange(
                "endDate",
                date ? format(date, "yyyy-MM-dd") : "",
              )
            }
            placeholderText="End Date"
          />
        </div>

        {/* Buttons */}
        <div className="col-12 col-md-auto d-flex align-items-end">
          <button className="btn btn-border btn-sm me-2" onClick={clearFilter}>
            Clear
          </button>
        </div>
      </div>

      {loading && (
        <div className="loader-container">
          <div className="loading">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      )}
      <div>
        {reportData.map((cls, idx) => {
          const classDates = getClassDates(cls.students);

          return (
            <div key={idx} className="mb-4 border rounded">
              <div
                className="p-2 fw-bold border-top-primary"
                style={{ fontSize: "13px" }}
              >
                {cls.classCode}
              </div>
              <div className="card shadow-sm table-responsive">
                <table className="table table-sm mb-0 align-middle custom-student-attendance-reports-table">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Student ID</th>
                      <th>Student Name</th>
                      {classDates.map((date) => (
                        <th key={date}>
                          {format(new Date(date), "dd-MM-yyyy")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cls.students.map((st, sIdx) => (
                      <tr key={sIdx}>
                        <td>{sIdx + 1}</td>
                        <td>{st.studentUniqueId || "-"}</td>
                        <td>
                          <Link
                            to={`/student/view/${st.studentId}`}
                            className="text-primary"
                            style={{ textDecoration: "none" }}
                          >
                            {st.studentName}
                          </Link>
                        </td>

                        {classDates.map((date) => {
                          const data = st.attendance?.[date];

                          return (
                            // <td key={date} className="text-start">
                            //   {data?.status === "R" ? (
                            //     <>
                            //       <div className="fw-bold text-warning">-</div>
                            //       <div
                            //         style={{ fontSize: "11px", color: "#666" }}
                            //       >
                            //         {data.replacementClassCode}
                            //       </div>
                            //     </>
                            //   ) : data?.status === "P" ? (
                            //     <span>-</span>
                            //   ) : (
                            //     ""
                            //   )}
                            // </td>
                            // <td key={date} className="text-start">
                            //   {data ? (
                            //     <>
                            //       {data.curriculumCode ? (
                            //         <div
                            //           style={{
                            //             fontSize: "11px",
                            //             color: "#212121",
                            //             backgroundColor: "yellow",
                            //           }}
                            //         >
                            //           {data.curriculumCode}
                            //         </div>
                            //       ) : (
                            //         "-"
                            //       )}

                            //       {data.status === "R" &&
                            //         data.replacementClassCode && (
                            //           <div
                            //             style={{
                            //               fontSize: "11px",
                            //               color: "#666",
                            //             }}
                            //           >
                            //             {data.replacementClassCode}
                            //           </div>
                            //         )}
                            //     </>
                            //   ) : (
                            //     <span className="fw-bold">-</span>
                            //   )}
                            // </td>
                            <td key={date} className="text-start">
                              {data ? (
                                <>
                                  {data.curriculumCode ? (
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#212121",
                                        backgroundColor: data.isReplacement
                                          ? "#ffeb3b"
                                          : "transparent",
                                        padding: "2px 4px",
                                        borderRadius: "3px",
                                        display: "inline-block",
                                      }}
                                    >
                                      {data.curriculumCode}
                                    </div>
                                  ) : (
                                    <span className="fw-bold">-</span>
                                  )}

                                  {data.isReplacement &&
                                    data.replacementClassCode && (
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#666",
                                        }}
                                      >
                                        {data.replacementClassCode}
                                      </div>
                                    )}
                                </>
                              ) : (
                                <span className="fw-bold">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default StudentAttendanceReport;
